import { useEffect, useMemo, useState } from "react";
import "./EmployeeManage.css";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:3000")
  .replace(/\/$/, "")
  .replace(/\/api$/, "");
const AVATAR_COLORS = ["#4338ca", "#7c3aed", "#16a34a", "#263012", "#2563eb", "#263012", "#0891b2", "#be185d", "#394420", "#9333ea"];
const DEPTS = ["Engineering", "Product", "Design", "Analytics", "HR", "Marketing", "Finance", "Sales", "Operations"];
const EMP_TYPES = ["Full-Time", "Part-Time", "Contract", "Intern"];

function readToken() {
  return localStorage.getItem("th_employer_token") || localStorage.getItem("th_token") || localStorage.getItem("token") || "";
}

function authFetch(path, opts = {}) {
  const token = readToken();
  const apiPath = path.startsWith("/api/") ? path : `/api${path.startsWith("/") ? path : `/${path}`}`;
  return fetch(`${API_BASE}${apiPath}`, {
    credentials: "include",
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

function fmt(n) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));
}

function fmtDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function initials(name) {
  return (name || "")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function mapEmployee(emp, index = 0) {
  const name = `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "Employee";
  return {
    id: emp.id,
    name,
    dept: emp.department || emp.dept || "",
    role: emp.designation || emp.role || "",
    type: emp.employment_type || emp.type || "Full-Time",
    joiningDate: emp.joining_date || emp.joiningDate || "",
    basicSalary: emp.basic_salary ?? emp.basicSalary ?? 0,
    payCycle: emp.pay_cycle || "Monthly",
    employee_code: emp.employee_code || "",
    email: emp.email || "",
    color: index % AVATAR_COLORS.length,
  };
}

const EmpAvatar = ({ emp, size = 36 }) => (
  <div
    className="emp-avatar"
    style={{ width: size, height: size, background: AVATAR_COLORS[emp.color % AVATAR_COLORS.length] }}
  >
    {initials(emp.name)}
  </div>
);

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  designation: "",
  department: "",
  joining_date: "",
  employment_type: "Full-Time",
  pay_cycle: "Monthly",
  basic_salary: "",
  employee_code: "",
  email: "",
};

export default function EmployeeManageApi() {
  const [employees, setEmployees] = useState([]);
  const [modal, setModal] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/payroll/employees");
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load employees.");
      setEmployees((Array.isArray(data.data) ? data.data : []).map(mapEmployee));
    } catch (err) {
      setMessage(err.message || "Could not load employees.");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return employees.filter((e) => {
      const deptMatch = deptFilter === "All" || e.dept === deptFilter;
      return (
        deptMatch &&
        (
          e.name.toLowerCase().includes(q) ||
          String(e.id).toLowerCase().includes(q) ||
          e.role.toLowerCase().includes(q)
        )
      );
    });
  }, [employees, search, deptFilter]);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    setModal("add");
  };

  const openEdit = (emp) => {
    setFormData({
      id: emp.id,
      first_name: emp.name.split(" ")[0] || "",
      last_name: emp.name.split(" ").slice(1).join(" ") || "",
      designation: emp.role || "",
      department: emp.dept || "",
      joining_date: emp.joiningDate || "",
      employment_type: emp.type || "Full-Time",
      pay_cycle: emp.payCycle || "Monthly",
      basic_salary: emp.basicSalary || "",
      employee_code: emp.employee_code || "",
      email: emp.email || "",
    });
    setErrors({});
    setModal("edit");
  };

  const setF = (key, val) => {
    setFormData((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!formData.first_name?.trim()) next.first_name = "Required";
    if (!formData.last_name?.trim()) next.last_name = "Required";
    if (!formData.department) next.department = "Required";
    if (!formData.joining_date) next.joining_date = "Required";
    if (!formData.basic_salary || Number(formData.basic_salary) <= 0) next.basic_salary = "Invalid salary";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  async function saveEmployee() {
    if (!validate()) return;
    setSaving(true);

    try {
      const method = modal === "add" ? "POST" : "PUT";
      const url = modal === "add" ? "/api/payroll/employees" : `/api/payroll/employees/${formData.id}`;
      const res = await authFetch(url, {
        method,
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not save employee.");
      setMessage(modal === "add" ? "Employee added successfully." : "Employee updated successfully.");
      setModal(null);
      await loadEmployees();
    } catch (err) {
      setMessage(err.message || "Could not save employee.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteEmployee(emp) {
    if (!window.confirm("Remove this employee from payroll?")) return;
    try {
      const res = await authFetch(`/api/payroll/employees/${emp.id}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not delete employee.");
      setEmployees((prev) => prev.filter((item) => item.id !== emp.id));
      setMessage("Employee removed.");
    } catch (err) {
      setMessage(err.message || "Could not delete employee.");
    }
  }

  return (
    <div className="payroll-page">
      <header className="page-header">
        <h1>Employee Management</h1>
        <div className="header-actions">
          <input
            type="search"
            placeholder="Search employee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
            <option value="All">All Departments</option>
            {DEPTS.map((d) => <option key={d}>{d}</option>)}
          </select>
          <button className="btn btn-primary" onClick={openAdd}>Add Employee</button>
        </div>
      </header>

      {message && <div className="empty" style={{ padding: "12px 16px", marginBottom: 12 }}>{message}</div>}

      <div className="table-wrap">
        <table className="emp-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Role</th>
              <th>Type</th>
              <th>Joining</th>
              <th>Salary</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="empty">Loading employees...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="empty">No employees found</td></tr>
            ) : filtered.map((emp) => (
              <tr key={emp.id}>
                <td>
                  <div className="emp-cell">
                    <EmpAvatar emp={emp} />
                    <div>
                      <b>{emp.name}</b>
                      <div className="emp-meta">{emp.id}</div>
                    </div>
                  </div>
                </td>
                <td>{emp.dept || "—"}</td>
                <td>{emp.role || "—"}</td>
                <td>{emp.type || "—"}</td>
                <td>{fmtDate(emp.joiningDate)}</td>
                <td>{fmt(emp.basicSalary)}</td>
                <td>
                  <button className="btn-xs" onClick={() => openEdit(emp)}>✎</button>
                  <button className="btn-xs btn-danger" onClick={() => deleteEmployee(emp)}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(modal === "add" || modal === "edit") && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{modal === "add" ? "Add Employee" : "Edit Employee"}</h2>
            <div className="form-grid">
              <div>
                <label>First Name</label>
                <input value={formData.first_name || ""} onChange={(e) => setF("first_name", e.target.value)} />
                {errors.first_name && <div className="error">{errors.first_name}</div>}
              </div>
              <div>
                <label>Last Name</label>
                <input value={formData.last_name || ""} onChange={(e) => setF("last_name", e.target.value)} />
                {errors.last_name && <div className="error">{errors.last_name}</div>}
              </div>
              <div>
                <label>Department</label>
                <select value={formData.department || ""} onChange={(e) => setF("department", e.target.value)}>
                  <option value="">Select</option>
                  {DEPTS.map((d) => <option key={d}>{d}</option>)}
                </select>
                {errors.department && <div className="error">{errors.department}</div>}
              </div>
              <div>
                <label>Designation</label>
                <input value={formData.designation || ""} onChange={(e) => setF("designation", e.target.value)} />
              </div>
              <div>
                <label>Joining Date</label>
                <input type="date" value={formData.joining_date || ""} onChange={(e) => setF("joining_date", e.target.value)} />
                {errors.joining_date && <div className="error">{errors.joining_date}</div>}
              </div>
              <div>
                <label>Employment Type</label>
                <select value={formData.employment_type || "Full-Time"} onChange={(e) => setF("employment_type", e.target.value)}>
                  {EMP_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label>Pay Cycle</label>
                <select value={formData.pay_cycle || "Monthly"} onChange={(e) => setF("pay_cycle", e.target.value)}>
                  <option>Monthly</option>
                  <option>Bi-Weekly</option>
                  <option>Weekly</option>
                </select>
              </div>
              <div>
                <label>Employee Code</label>
                <input value={formData.employee_code || ""} onChange={(e) => setF("employee_code", e.target.value)} />
              </div>
              <div>
                <label>Email</label>
                <input type="email" value={formData.email || ""} onChange={(e) => setF("email", e.target.value)} />
              </div>
              <div>
                <label>Basic Salary (₹)</label>
                <input type="number" value={formData.basic_salary || ""} onChange={(e) => setF("basic_salary", e.target.value)} />
                {errors.basic_salary && <div className="error">{errors.basic_salary}</div>}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={saveEmployee} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
